import { MathUtils } from "three";
import { clone } from "./helpers";
export var SceneManagerEvent;
(function (SceneManagerEvent) {
    SceneManagerEvent["Select"] = "select";
    SceneManagerEvent["Add"] = "add";
    SceneManagerEvent["Update"] = "update";
    SceneManagerEvent["Remove"] = "remove";
    SceneManagerEvent["Floor"] = "floor";
    SceneManagerEvent["Size"] = "size";
    SceneManagerEvent["Banner"] = "banner";
    SceneManagerEvent["Clear"] = "clear";
    SceneManagerEvent["Truss"] = "truss";
})(SceneManagerEvent || (SceneManagerEvent = {}));
export var WallConfig;
(function (WallConfig) {
    WallConfig["None"] = "none";
    WallConfig["Back"] = "back";
    WallConfig["Left"] = "left";
    WallConfig["Right"] = "right";
    WallConfig["U"] = "u";
})(WallConfig || (WallConfig = {}));
export const maxTrussHeight = 600;
class SceneManager {
    constructor() {
        this.items = new Map();
        this.listeners = new Map();
        this.textures = new Map();
        this._selectedItems = [];
        this._selectionOptions = new Map();
        this._trussHeight = maxTrussHeight;
    }
    set id(id) {
        if (id && !this._id) {
            this._id = id;
        }
    }
    get id() {
        return this._id;
    }
    get width() {
        return this._width;
    }
    get depth() {
        return this._depth;
    }
    get height() {
        return this._height;
    }
    get trussHeight() {
        return this._trussHeight;
    }
    get floor() {
        return this._floor;
    }
    get selectedItems() {
        return this._selectedItems.map(i => this.getItem(i));
    }
    set trussHeight(v) {
        this._trussHeight = v;
        this.notifyListeners(SceneManagerEvent.Truss, { trussHeight: this._trussHeight });
    }
    set banner(b) {
        this._banner = clone(b);
        this.notifyListeners(SceneManagerEvent.Banner, { banner: clone(this._banner) });
    }
    get banner() {
        var _a;
        return clone((_a = this._banner) !== null && _a !== void 0 ? _a : {});
    }
    /**
     * set the floor material
     */
    setFloor(floor) {
        this._floor = floor;
        this.notifyListeners(SceneManagerEvent.Floor, { floor });
    }
    /**
     * set the bounds of the scene
     * @param width - the width of the scene im meters
     * @param depth - the depth of the scene in meters
     * @param height - the height of the walls in meters
     * @param config - the wallconfig
     */
    setBounds(width, depth, height, config = WallConfig.None) {
        this._width = width;
        this._depth = depth;
        this._height = height;
        this._wallConfig = config;
        this.notifyListeners(SceneManagerEvent.Size, { wallConfig: this._wallConfig, size: { width, depth, height } });
    }
    /**
     * Save a File as texture
     * @param file - The File pointing to the image to be used as texture
     * @returns the key of the file that can be used to reference it
     */
    putTexture(file) {
        const { name } = file;
        if (!this.textures.has(name)) {
            this.textures.set(name, file);
        }
        return name;
    }
    /**
     * Fetches a texture from the store and returns the image as a dataurl or null if the
     * texture doesn't exist.
     * @param name - the name of the texture to fetch
     * @returns a promise that will resolve into a dataurl containing the texture image or null
     */
    async getTexture(name) {
        if (this.textures.has(name)) {
            const texture = this.textures.get(name);
            return new Promise(async (resolve) => {
                const reader = new FileReader();
                reader.addEventListener("load", function () {
                    resolve(reader.result.toString());
                }, false);
                if (texture instanceof File) {
                    reader.readAsDataURL(texture);
                }
                else {
                    const blob = await fetch(texture).then(r => r.blob());
                    reader.readAsDataURL(blob);
                }
            });
        }
        return null;
    }
    /**
     * Removes texture from manager but checks first if it is still used by an known item
     * @param name - The name of the texture
     * @return true if the texture was actually removed
     */
    removeTexture(name) {
        if (this.textures.has(name)) {
            const remove = !Array.from(this.items.entries()).some(([id, item]) => item.texture === name);
            if (remove) {
                this.textures.delete(name);
            }
            return remove;
        }
        return true;
    }
    /**
     * get an item by id
     * @param id - The item's id you want to fetch
     * @returns The requested item or null if it doesn't exist
     */
    getItem(id) {
        if (id && this.items.has(id)) {
            const selectionOptions = this._selectionOptions.get(id);
            return Object.assign(Object.assign({}, this.items.get(id)), { selectionOptions });
        }
        else {
            return null;
        }
    }
    /**
     * Put an item into the manager. If an item with the same id already exists it will be updated
     * with the given item's values.
     * @param item - A new or changed item.
     */
    putItem(item) {
        if (item.id && this.items.has(item.id)) {
            const original = this.items.get(item.id);
            this.items.set(item.id, Object.assign({}, item));
            const changed = [];
            for (let k in item) {
                if (original[k] !== item[k]) {
                    changed.push(k);
                }
            }
            this.notifyListeners(SceneManagerEvent.Update, {
                item: Object.assign({}, item),
                changed
            });
        }
        else {
            item.id = MathUtils.generateUUID();
            this.items.set(item.id, Object.assign({}, item));
            this.notifyListeners(SceneManagerEvent.Add, { item: Object.assign({}, item) });
        }
    }
    /**
     * remove an item from the manager
     * @param id - The id of the item to remove
     */
    removeItem(id) {
        const itemToRemove = this.items.get(id);
        if (itemToRemove) {
            this.items.delete(id);
            this.notifyListeners(SceneManagerEvent.Remove, { item: itemToRemove });
            if (this._selectedItems.includes(id)) {
                this.selectItem();
            }
        }
    }
    /**
     * Mark a single or multiple item as selected. If the given id is null or undefined the current selection is cleared.
     * @param id - The id of the item to select or an array of ids.
     * @param append - Append the item(s) to the current list of selected items.
     * @param options - An object of any options that will be passed along with the selection
     */
    selectItem(id, append = false, options) {
        if (id) {
            if (!append) {
                this._selectedItems = [];
                this._selectionOptions.clear();
            }
            if (Array.isArray(id)) {
                id.forEach(i => this._selectSingleItem(i, options));
            }
            else {
                this._selectSingleItem(id, options);
            }
            this.notifyListeners(SceneManagerEvent.Select, { items: this.selectedItems });
        }
        else {
            this._selectedItems = [];
            this._selectionOptions.clear();
            this.notifyListeners(SceneManagerEvent.Select, { items: null });
        }
    }
    _selectSingleItem(id, options) {
        if (id && this.items.has(id) && !this._selectedItems.includes(id)) {
            this._selectedItems.push(id);
            if (options)
                this._selectionOptions.set(id, options);
        }
    }
    notifyListeners(event, payload) {
        const listeners = this.listeners.get(event) || [];
        listeners.forEach(l => l(payload));
    }
    /**
     * Add an event listener callback which will be called if an event of that type is triggered
     * @param event - The type of event to listen for
     * @param fn - The callback function to call if such an event is triggered
     */
    addListener(event, fn) {
        const listeners = this.listeners.get(event) || [];
        listeners.push(fn);
        this.listeners.set(event, listeners);
    }
    /**
     * Remove an event listener from the manager. The function has to be the same pointer used for adding the listener.
     * @param event - The type of event to stop listening for
     * @param fn - The callback function to remove
     */
    removeListener(event, fn) {
        const listeners = this.listeners.get(event) || [];
        const index = listeners.indexOf(fn);
        if (index > -1) {
            listeners.splice(index, 1);
        }
    }
    /**
     * clear all items from a scene
     */
    clear() {
        this._id = undefined;
        this.textures.clear();
        this.items.clear();
        this._trussHeight = maxTrussHeight;
        this.notifyListeners(SceneManagerEvent.Clear);
    }
    /**
     * serialize the scene to a json string
     */
    serialize() {
        return JSON.stringify({
            id: this._id,
            floor: this.floor,
            width: this.width,
            depth: this.depth,
            height: this.height,
            trussHeight: this.trussHeight,
            items: Array.from(this.items.values()).map(si => (Object.assign({}, si))),
            textures: Array.from(this.textures.entries()).map(([k, f]) => typeof f === "string" ? k : f.name),
            banner: this.banner,
        });
    }
    restore(data) {
        if (typeof data === "string") {
            data = JSON.parse(data);
        }
        this.clear();
        this.setBounds(data.width, data.depth, data.height, WallConfig.None);
        this.trussHeight = data.trussHeight;
        this.setFloor(data.floor);
        data.items.forEach(i => {
            this.putItem(i);
        });
        this.textures.clear();
        data.textures.forEach(t => {
            this.textures.set(t, `/projects/${data.id}/${t}`);
        });
        this.banner = data.banner;
        this._id = data.id;
    }
}
export default SceneManager;
